import randomBytes from 'randombytes';

export const RFC4122_PATTERN =
  /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;

export const trimGuid = (guid: string, maxLength: number): string => {
  let str = '';
  let i = guid.length - 1;
  do {
    const ch = guid[i];
    if (ch !== '-') {
      str = ch + str;
    }
    i--;
  } while (str.length < maxLength && i >= 0);
  return str;
};

// Returns whether the given string represents a full guid
export const isGuid = (guid: string): boolean => {
  return RFC4122_PATTERN.test(guid);
};

const randomHex4 = (): string => {
  return randomBytes(2).toString('hex');
};

/**
 * Generates a guid.
 * @param  {Number} maxLength The maximum length of the guid. If specified, only the last N chars from the full 36-char guid will be returned.
 * @return {String}
 */
export const generateGuid = (maxLength?: number): string => {
  const buf = Array(8).fill(0).map(randomHex4);
  const guid =
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    buf[0] +
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    buf[1] +
    '-' +
    buf[2] +
    '-' +
    buf[3] +
    '-' +
    buf[4] +
    '-' +
    buf[5] +
    buf[6] +
    buf[7];

  // If there is a max length specified, truncate the left part of the guid
  if (maxLength) {
    return trimGuid(guid, maxLength);
  }

  return guid;
};

export const incrementGuid = (
  guid: string,
  incrementAmount: number = 1
): string =>
  guid.slice(0, 24) +
  (
    parseInt(guid.slice(24), 16) + // convert last part of guid to int
    incrementAmount + // increment
    0x1000000000000
  ) // add padding
    .toString(16) // convert back to base 16
    .slice(1); // remove leading “1” from the padding we added above

/** A class to create GUIDs.
 * It takes an optional initial GUID as a parameter and increments the last
 * part of the GUID on each call to getNext(). If no initial GUID is passed in,
 * a random GUID is generated.
 *
 * @param {string} initialGuid - A hexadecimal string representing the initial GUID.
 * @param {number} incrementAmount - The amount by which to increment the last part of the GUID on each call to getNext().
 *
 * @example
 * const gen = new GuidGenerator('aefcc22f-0319-4725-abfc-d0ce42e07665');
 * console.log(gen.getNext()); // Prints: 'aefcc22f-0319-4725-abfc-d0ce42e07666'
 * console.log(gen.getNext()); // Prints: 'aefcc22f-0319-4725-abfc-d0ce42e07667'
 * // and so on
 */
export class GuidGenerator {
  protected guid: string;
  protected readonly increment: number;

  /**
   * @param {string} initialGuid - A hexadecimal string representing the initial GUID.
   * @param {number} increment - The amount by which to increment the last part of the GUID on each call to getNext().
   */
  constructor(initialGuid: string = generateGuid(), increment: number = 1) {
    this.guid = initialGuid;
    this.increment = increment;
  }

  /**
   * @returns {string} - The next GUID in the sequence.
   */
  public getNext(): string {
    this.guid = incrementGuid(this.guid, this.increment);
    return this.guid;
  }
}
